'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';
import { Badge } from '@/components/Badge';
import { MfaStatusBadge } from '@/components/Badge/MfaStatusBadge';
import { ProductLogoMediumSoftware } from '@/components/Logo';
import { Text } from '@/components/Typography';
import { defaultNS, useTranslation } from '@/lib/i18n';
import type { MemberAccount } from '@/mocks/db/types';
import { MY_PORTAL_PERSON_ID } from '../-shared/-api';
import { ledgerColumn } from '../-shared/-components/ledgerColumn';
import { LedgerContent } from '../-shared/-components/LedgerContent';
import { LedgerDataTable } from '../-shared/-components/LedgerDataTable';
import { type LedgerFilterTab, LedgerFilterTabs } from '../-shared/-components/LedgerFilterTabs';
import { LedgerHeader } from '../-shared/-components/LedgerHeader';
import { PersonCell } from '../-shared/-components/ResourceCells';
import { Field, FieldGrid, ResourceDetailCard } from '../-shared/-components/ResourceDetailCard';
import { useMemberAccounts, usePortalPersons } from '../-shared/-query';
import { formatDate, toMapById } from '../-shared/-utils';
const ACCOUNTS_PATH = '/individual/portal/lookup/accounts';
const LOOKUP_PATH = '/individual/portal/lookup';
const accountsSearchSchema = z.object({
  scope: z.enum(['self', 'all']).catch('self'),
  id: z.string().optional()
});
export const Route = createFileRoute('/_individual/individual/portal/lookup/_ledger/accounts/')({
  validateSearch: accountsSearchSchema,
  component: AccountsLookupPage
});
function AccountsLookupPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.lookupResources'
  });
  const {
    t: ct
  } = useTranslation(defaultNS, {
    keyPrefix: 'common.breadcrumbs'
  });
  const {
    t: th
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.home'
  });
  const {
    scope,
    id
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    data: accounts,
    isPending
  } = useMemberAccounts();
  const {
    data: persons
  } = usePortalPersons();
  const personsById = useMemo(() => toMapById(persons), [persons]);
  const selected = id != null ? accounts?.find(a => a.id === id) ?? null : null;
  const rows = useMemo(() => {
    const all = accounts ?? [];
    return scope === 'self' ? all.filter(a => a.holderPersonId === MY_PORTAL_PERSON_ID) : all;
  }, [accounts, scope]);
  const mfaLabel = (enabled: boolean) => enabled ? t('accounts.mfa.enabled') : t('accounts.mfa.disabled');
  if (selected != null) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('accounts.title'),
        link: {
          to: ACCOUNTS_PATH,
          search: {
            scope
          }
        }
      }, {
        label: selected.app
      }]} Logo={ProductLogoMediumSoftware} title={selected.app} subtitle={selected.account} />
        <LedgerContent>
          <ResourceDetailCard title={t('accounts.detail.basicInfo')}>
            <FieldGrid>
              <Field label={t('accounts.detail.app')} value={selected.app} />
              <Field label={t('accounts.detail.account')} value={selected.account} />
              <Field label={t('accounts.detail.displayName')} value={selected.displayName} />
              <Field label={t('accounts.detail.role')} value={<Badge variant="outline">{selected.role}</Badge>} />
              <Field label={t('accounts.detail.holder')} value={<PersonCell person={personsById.get(selected.holderPersonId)} />} />
              <Field label={t('accounts.detail.mfa')} value={<MfaStatusBadge enabled={selected.mfaEnabled} label={mfaLabel(selected.mfaEnabled)} />} />
              <Field label={t('accounts.detail.createdAt')} value={formatDate(selected.createdAt)} />
              <Field label={t('accounts.detail.lastSignIn')} value={formatDate(selected.lastSignIn)} />
            </FieldGrid>
          </ResourceDetailCard>
        </LedgerContent>
      </>;
  }
  const columns = [ledgerColumn<MemberAccount>({
    id: 'app',
    header: t('accounts.headers.app'),
    cell: a => <Text variant="label">{a.app}</Text>
  }), ledgerColumn<MemberAccount>({
    id: 'account',
    header: t('accounts.headers.account'),
    cell: a => a.account
  }), ledgerColumn<MemberAccount>({
    id: 'displayName',
    header: t('accounts.headers.displayName'),
    cell: a => a.displayName
  }), ledgerColumn<MemberAccount>({
    id: 'role',
    header: t('accounts.headers.role'),
    cell: a => <Badge variant="outline">{a.role}</Badge>
  }), ledgerColumn<MemberAccount>({
    id: 'createdAt',
    header: t('accounts.headers.createdAt'),
    cell: a => formatDate(a.createdAt)
  }), ledgerColumn<MemberAccount>({
    id: 'lastSignIn',
    header: t('accounts.headers.lastSignIn'),
    cell: a => formatDate(a.lastSignIn)
  }), ledgerColumn<MemberAccount>({
    id: 'mfa',
    header: t('accounts.headers.mfa'),
    cell: a => <MfaStatusBadge enabled={a.mfaEnabled} label={mfaLabel(a.mfaEnabled)} />
  })];
  const scopeTabs: LedgerFilterTab<'self' | 'all'>[] = [{
    value: 'self',
    label: t('accounts.tabs.self')
  }, {
    value: 'all',
    label: t('accounts.tabs.all')
  }];
  return <>
      <LedgerHeader breadcrumbs={[{
      label: ct('home'),
      link: {
        to: '/'
      }
    }, {
      label: th('lookup.title'),
      link: {
        to: LOOKUP_PATH
      }
    }, {
      label: t('accounts.title')
    }]} Logo={ProductLogoMediumSoftware} title={t('accounts.title')} />
      <LedgerContent className="h-full">
        <LedgerDataTable<MemberAccount> fillHeight columns={columns} data={rows} isPending={isPending} filterTabs={<LedgerFilterTabs tabs={scopeTabs} value={scope} onSelect={s => void navigate({
        search: prev => ({
          ...prev,
          scope: s
        })
      })} ariaLabel={t('common.filterTabsLabel')} />} getRowId={a => a.id} getSearchText={a => `${a.app} ${a.account} ${a.displayName}`} searchPlaceholder={t('common.search')} onRowSelect={a => void navigate({
        search: prev => ({
          ...prev,
          id: a.id
        })
      })} emptyContent={<Text variant="p" className="text-neutral-muted-on-fill">
              {t('common.empty')}
            </Text>} />
      </LedgerContent>
    </>;
}